'use client';

// FeedbackButtons - Thumbs up/down feedback UI for bot responses
// Story 4.4: Attendee Feedback on Answer Quality
// Story 4.5: Mobile-Optimized Bot Interface - touch targets & hover states
// Non-intrusive inline feedback below each bot message

import { useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { useTranslations } from 'next-intl';
import type { FeedbackRating } from './types';

export type { FeedbackRating };

export interface FeedbackButtonsProps {
  messageId: string;
  currentRating?: FeedbackRating;
  onFeedbackSubmit: (
    messageId: string,
    rating: FeedbackRating,
    comment?: string
  ) => void;
  disabled?: boolean;
}

const REASON_KEYS = ['incorrect', 'notRelevant', 'tooVague', 'other'] as const;

export function FeedbackButtons({
  messageId,
  currentRating,
  onFeedbackSubmit,
  disabled = false,
}: FeedbackButtonsProps) {
  const t = useTranslations('consumer.feedback');
  const [showReasons, setShowReasons] = useState(
    currentRating === 'negative'
  );

  const handleThumbUp = () => {
    setShowReasons(false);
    onFeedbackSubmit(messageId, 'positive');
  };

  const handleThumbDown = () => {
    setShowReasons(true);
    onFeedbackSubmit(messageId, 'negative');
  };

  const handleReasonSelect = (reasonKey: string) => {
    const reasonText = t(`reasons.${reasonKey}`);
    onFeedbackSubmit(messageId, 'negative', reasonText);
    setShowReasons(false);
  };

  const isPositive = currentRating === 'positive';
  const isNegative = currentRating === 'negative';

  return (
    <Box sx={{ mt: 0.5 }}>
      {/* Thumbs row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            opacity: 0.7,
            userSelect: 'none',
          }}
        >
          {t('wasThisHelpful')}
        </Typography>

        <IconButton
          size="small"
          onClick={handleThumbUp}
          disabled={disabled}
          aria-label={t('yes')}
          sx={{
            color: isPositive ? 'primary.main' : 'text.secondary',
            opacity: isPositive ? 1 : 0.7,
            // 48x48px minimum touch target (NFR54)
            p: 1.5,
            minWidth: 48,
            minHeight: 48,
            // Touch-friendly: only show hover on pointer devices
            '@media (hover: hover)': {
              '&:hover': { opacity: 1 },
            },
            // Active state for touch feedback
            '&:active': {
              transform: 'scale(0.95)',
            },
            // Prevent double-tap zoom on mobile
            touchAction: 'manipulation',
          }}
        >
          {isPositive ? (
            <ThumbUpIcon fontSize="small" />
          ) : (
            <ThumbUpOutlinedIcon fontSize="small" />
          )}
        </IconButton>

        <IconButton
          size="small"
          onClick={handleThumbDown}
          disabled={disabled}
          aria-label={t('no')}
          sx={{
            color: isNegative ? 'error.main' : 'text.secondary',
            opacity: isNegative ? 1 : 0.7,
            // 48x48px minimum touch target (NFR54)
            p: 1.5,
            minWidth: 48,
            minHeight: 48,
            // Touch-friendly: only show hover on pointer devices
            '@media (hover: hover)': {
              '&:hover': { opacity: 1 },
            },
            // Active state for touch feedback
            '&:active': {
              transform: 'scale(0.95)',
            },
            // Prevent double-tap zoom on mobile
            touchAction: 'manipulation',
          }}
        >
          {isNegative ? (
            <ThumbDownIcon fontSize="small" />
          ) : (
            <ThumbDownOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      {/* Follow-up reason chips (shown after thumbs down) */}
      {showReasons && (
        <Box sx={{ mt: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
          >
            {t('whatWentWrong')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {REASON_KEYS.map((key) => (
              <Chip
                key={key}
                label={t(`reasons.${key}`)}
                variant="outlined"
                size="small"
                onClick={() => handleReasonSelect(key)}
                disabled={disabled}
                sx={{
                  fontSize: '0.75rem',
                  // Minimum 48px height for touch targets (NFR54)
                  height: 48,
                  // Touch-friendly: only show hover on pointer devices
                  '@media (hover: hover)': {
                    '&:hover': { backgroundColor: 'action.hover' },
                  },
                  // Active state for touch feedback
                  '&:active': {
                    transform: 'scale(0.98)',
                  },
                  touchAction: 'manipulation',
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default FeedbackButtons;
